import { deltaSign, formatNumber } from "../utils/format.js";
import { accentVars } from "../utils/palette.js";
import styles from "./ComparisonEquipment.module.css";

const EQUIPMENT_LABELS = {
  barbell: "Barbell", dumbbell: "Dumbbell", cable: "Cable", machine: "Machine",
  bodyweight: "Bodyweight", band: "Band", "resistance band": "Band",
  kettlebell: "Kettlebell", plate: "Plate", "ez-bar": "EZ Bar", "ez bar": "EZ Bar",
  "smith machine": "Smith Machine", "assisted bodyweight": "Assisted",
  "weighted bodyweight": "Weighted BW", other: "Other"
};

function label(id) {
  return EQUIPMENT_LABELS[id] || id.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function DeltaChip({ delta, hasPrev }) {
  if (!hasPrev) return null;
  const dir = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  return (
    <span className={`${styles.delta} ${styles[dir]}`}>
      {deltaSign(dir)}{delta !== 0 ? ` ${delta > 0 ? "+" : ""}${delta}` : ""}
    </span>
  );
}

function setCounts(equipment) {
  return Object.fromEntries((equipment || []).map((e) => [e.id, e.setCount]));
}

export default function ComparisonEquipment({ profiles }) {
  if (!profiles?.length) return null;

  const series = profiles.map((profile) => {
    const sets = setCounts(profile.analytics.equipmentBreakdown);
    const prev = setCounts(profile.analytics.previous?.equipmentBreakdown);
    return {
      username: profile.username,
      accent: profile.accent,
      sets,
      prev,
      total: Object.values(sets).reduce((s, v) => s + v, 0) || 1,
      prevTotal: Object.values(prev).reduce((s, v) => s + v, 0)
    };
  });

  const allIds = [...new Set(series.flatMap((s) => Object.keys(s.sets)))];
  const totalFor = (id) => series.reduce((sum, s) => sum + (s.sets[id] || 0), 0);
  allIds.sort((x, y) => totalFor(y) - totalFor(x));

  const rows = allIds.map((id) => ({
    id,
    label: label(id),
    cells: series.map((s) => ({
      username: s.username,
      accent: s.accent,
      sets: s.sets[id] || 0,
      pct: ((s.sets[id] || 0) / s.total) * 100,
      delta: (s.sets[id] || 0) - (s.prev[id] || 0),
      hasPrev: s.prevTotal > 0
    }))
  }));
  const maxPct = Math.max(...rows.flatMap((r) => r.cells.map((c) => c.pct)), 1);

  return (
    <div className={styles.panel}>
      <div className={styles.heading}>
        <span className={styles.label}>Equipment</span>
        <span className={styles.note}>% of total sets</span>
      </div>
      <div className={styles.legend}>
        {series.map((s) => (
          <span key={s.username} className={styles.legendItem} style={accentVars(s.accent)}>@{s.username}</span>
        ))}
      </div>
      {rows.length === 0 ? (
        <div className={styles.empty}>No equipment data</div>
      ) : (
        <div className={styles.list}>
          {rows.map(({ id, label, cells }) => (
            <div key={id} className={styles.row}>
              <span className={styles.equip}>{label}</span>
              <div className={styles.sides}>
                {cells.map((cell) => (
                  <div key={cell.username} className={styles.side} style={accentVars(cell.accent)}>
                    <div className={styles.track}>
                      <div className={styles.fill} style={{ width: `${(cell.pct / maxPct) * 100}%` }} />
                    </div>
                    <span className={styles.pct}>{Math.round(cell.pct)}%</span>
                    <span className={styles.sets}>{formatNumber(cell.sets)}</span>
                    <DeltaChip delta={cell.delta} hasPrev={cell.hasPrev} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
